import { IMAGE_CONFIG } from '../../config/appConfig';

/**
 * Compress an image data URL to reduce payload size using adaptive compression
 *
 * COMPRESSION STRATEGY:
 * - Stage 1: Try to compress using initialQuality, reducing quality iteratively until under maxSizeMB
 * - Stage 2: If still too large, reduce dimensions (width/height *= 0.8) until under maxSizeMB
 * - This balances image quality (for face recognition accuracy) with upload performance
 *
 * @param {string} dataUrl - Base64 encoded image data URL
 * @param {number} maxSizeMB - Hard limit on file size in MB (typically 7MB for fast upload)
 * @param {number} initialQuality - Starting compression quality 0.1-1.0 (network-aware: 0.6-0.9)
 * @returns {Promise<string>} Compressed image data URL
 */
export async function compressImage(
  dataUrl,
  maxSizeMB = IMAGE_CONFIG.MAX_SIZE_MB,
  initialQuality = IMAGE_CONFIG.INITIAL_QUALITY
) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Estimate size in MB (base64 is ~33% larger than binary)
      const estimateSize = (dataUrl) => (dataUrl.length * 0.75) / (1024 * 1024);

      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Start with original dimensions
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels until we're under the size limit, starting from user's preference
      let quality = initialQuality;
      let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      let attempts = 0;

      while (estimateSize(compressedDataUrl) > maxSizeMB && quality > 0.1) {
        quality -= IMAGE_CONFIG.QUALITY_STEP;
        compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        attempts++;
      }

      if (attempts > 0) {
      }

      // If still too large, reduce dimensions
      let dimensionReductions = 0;
      while (estimateSize(compressedDataUrl) > maxSizeMB && width > 400) {
        width *= 0.8;
        height *= 0.8;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        dimensionReductions++;
      }

      if (dimensionReductions > 0) {
      }

      resolve(compressedDataUrl);
    };
    img.src = dataUrl;
  });
}
